import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Leaf, Mail, Lock, User, ArrowRight, Ticket, CheckCircle2, XCircle } from "lucide-react";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function Register() {
  const { register } = useAuth();
  const [params] = useSearchParams();
  const location = useLocation();
  const preCode = (params.get("code") || "").toUpperCase();
  const returnTo = params.get("returnTo") || location.state?.returnTo || "/onboarding/profile";

  const [invitationCode, setInvitationCode] = useState(preCode);
  const [validated, setValidated] = useState(null); // null | {code,label,remaining} | false
  const [checking, setChecking] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const validate = async (raw) => {
    const code = (raw || "").trim().toUpperCase();
    if (!code) { setValidated(null); return; }
    setChecking(true);
    try {
      const { data } = await api.get(`/invitations/validate/${encodeURIComponent(code)}`);
      setValidated(data);
    } catch (err) {
      setValidated(false);
      toast.error(formatApiError(err.response?.data?.detail) || "Código de convite inválido");
    } finally { setChecking(false); }
  };

  useEffect(() => {
    if (preCode) validate(preCode);
    // Persist invitation code for Google Auth callback
    if (preCode) sessionStorage.setItem("caring:invitation_code", preCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!validated || !validated.code) {
      toast.error("Precisa de um código de convite válido para criar conta");
      return;
    }
    setBusy(true);
    try {
      await register({ email, password, name, invitation_code: validated.code });
      sessionStorage.removeItem("caring:invitation_code");
      navigate(returnTo);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const persistCodeForGoogle = () => {
    if (validated && validated.code) sessionStorage.setItem("caring:invitation_code", validated.code);
  };

  const inputCls = "w-full pl-10 pr-3 py-3 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)]";

  return (
    <div className="min-h-screen bg-[var(--bone)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="eyebrow">Aderir por convite</div>
          <h1 className="font-heading text-4xl mt-2 text-[var(--ink)]">Crie a sua conta</h1>
          <p className="text-[var(--ink-soft)] mt-2">Já tem conta? <Link to="/login" className="text-[var(--forest)] underline" data-testid="link-to-login">Entrar</Link></p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {/* Invitation code */}
            <div>
              <label className="text-sm font-medium">Código de convite</label>
              <div className="mt-1.5 relative">
                <Ticket className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]"/>
                <input
                  data-testid="register-invitation-input"
                  value={invitationCode}
                  onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                  onBlur={(e) => validate(e.target.value)}
                  required
                  placeholder="Ex: CARING2026"
                  className={`${inputCls} uppercase tracking-widest font-mono`}
                />
              </div>
              {checking && <div className="mt-1 text-xs text-[var(--ink-soft)]">A validar…</div>}
              {validated && validated.code && (
                <div className="mt-1 text-xs text-[var(--success)] flex items-center gap-1" data-testid="invitation-valid">
                  <CheckCircle2 className="w-3 h-3"/> Convite válido{validated.label ? ` · ${validated.label}` : ""}{validated.remaining ? ` · ${validated.remaining} restantes` : ""}
                </div>
              )}
              {validated === false && (
                <div className="mt-1 text-xs text-[var(--terracotta)] flex items-center gap-1" data-testid="invitation-invalid">
                  <XCircle className="w-3 h-3"/> Código inválido ou esgotado
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">Nome</label>
              <div className="mt-1.5 relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]"/>
                <input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="O seu nome" className={inputCls}/>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <div className="mt-1.5 relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]"/>
                <input type="email" data-testid="register-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="voce@exemplo.pt" className={inputCls}/>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Palavra-passe</label>
              <div className="mt-1.5 relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]"/>
                <input type="password" data-testid="register-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="Mínimo 6 caracteres" className={inputCls}/>
              </div>
            </div>
            <button
              type="submit"
              disabled={busy || !validated || !validated.code}
              className="pill-btn pill-primary w-full justify-center !py-3.5"
              data-testid="register-submit-btn"
            >
              {busy ? "A criar conta…" : "Continuar"} <ArrowRight className="w-4 h-4"/>
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-[var(--ink-soft)]">
            <div className="flex-1 h-px bg-[var(--border)]" />
            <div>ou com Google (requer convite válido acima)</div>
            <div className="flex-1 h-px bg-[var(--border)]" />
          </div>
          <div onClick={persistCodeForGoogle}>
            <GoogleAuthButton label="Continuar com Google" testId="register-google-btn" />
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-col justify-between p-14 bg-[var(--forest)] text-white relative overflow-hidden order-1 lg:order-2">
        <div className="absolute inset-0 opacity-10 paper-noise" aria-hidden />
        <Link to="/" className="flex items-center gap-3 relative z-10">
          <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 grid place-items-center"><Leaf className="w-5 h-5"/></div>
          <div>
            <div className="font-heading text-xl">CARING</div>
            <div className="eyebrow text-white/70">Referências</div>
          </div>
        </Link>
        <div className="relative z-10">
          <div className="eyebrow text-white/70">Rede fechada</div>
          <h2 className="font-heading text-5xl mt-3 leading-tight">Programa exclusivo<br/>por convite.</h2>
          <p className="text-white/80 mt-6 max-w-md">A CARING atribuiu-lhe um código único. Introduza-o para começar a recomendar candidatos e receber 15 € por cada contratação.</p>
        </div>
        <div className="relative z-10 text-sm text-white/60">© {new Date().getFullYear()} CARING</div>
      </div>
    </div>
  );
}
